import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';

function StudentLibraryCard({ student }) {
  const books = student?.recent_books || [];
  return (
    <Box sx={{ borderRadius: 4, border: '3px solid #a9ceff', background: 'linear-gradient(180deg, #f3f9ff 0%, #e1efff 100%)', boxShadow: '0 5px 0 rgba(37,99,235,0.13)', p: { xs: 1.5, sm: 2 }, minHeight: 210 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#1e3a8a', fontWeight: 1000, fontSize: { xs: 21, sm: 24 }, lineHeight: 1.1, overflowWrap: 'anywhere' }}>
            {student?.display_name || 'Öğrenci'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
            <Chip icon={<AutoStoriesRoundedIcon />} label={`${Number(student?.total_books || 0)} kitap`} sx={{ bgcolor: '#fff', color: '#1d4ed8', fontWeight: 1000 }} />
            <Chip label={`${Number(student?.total_pages || 0)} sayfa`} sx={{ bgcolor: '#e0f2fe', color: '#075985', fontWeight: 1000 }} />
          </Box>
        </Box>
        <MenuBookRoundedIcon aria-hidden="true" sx={{ fontSize: { xs: 48, sm: 56 }, color: '#2563eb', filter: 'drop-shadow(0 4px 0 rgba(37,99,235,0.18))' }} />
      </Box>

      {books.length > 0 ? (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: '18px 0 0', display: 'grid', gap: 0.7 }}>
          {books.map((book, index) => (
            <Box component="li" key={`${book.title}-${index}`} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.7, color: '#1e40af', fontWeight: 800, lineHeight: 1.25 }}>
              <Box component="span" aria-hidden="true">📖</Box>
              <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
                {book.title}{book.page_count ? ` · ${book.page_count} sayfa` : ''}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography sx={{ color: '#475569', fontWeight: 700, mt: 2.2 }}>Henüz tamamlanan kitap yok.</Typography>
      )}
    </Box>
  );
}

export default function LibraryReadOnlyDialog({ open, onClose, loading, error, library }) {
  const classCode = library?.class?.code;
  const students = library?.students || [];

  return (
    <Dialog fullScreen open={open} onClose={onClose} aria-labelledby="library-dialog-title">
      <Box sx={{ minHeight: '100%', bgcolor: '#f4faff', backgroundImage: 'radial-gradient(circle at 5% 98%, rgba(147,197,253,0.34) 0 120px, transparent 121px), radial-gradient(circle at 96% 4%, rgba(191,219,254,0.44) 0 100px, transparent 101px)' }}>
        <DialogTitle id="library-dialog-title" sx={{ bgcolor: '#1d4ed8', color: '#fff', minHeight: 78, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, boxShadow: '0 5px 0 rgba(30,64,175,0.22)' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="div" sx={{ fontSize: { xs: 24, sm: 32 }, fontWeight: 1000, lineHeight: 1.05 }}>
              {classCode ? `${classCode} Sınıfının Kütüphanesi` : 'Kütüphanemiz'}
            </Typography>
            <Typography component="div" sx={{ mt: 0.4, fontWeight: 800, fontSize: { xs: 14, sm: 16 }, color: '#dbeafe' }}>
              Salt okunur sınıf görünümü
            </Typography>
          </Box>
          <IconButton aria-label="Kütüphanemizi kapat" onClick={onClose} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.16)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ maxWidth: 1540, width: '100%', mx: 'auto', px: { xs: 1.4, sm: 2.5, md: 4 }, py: { xs: 2, sm: 3 } }}>
          {loading && (
            <Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center', gap: 1.5 }}>
              <CircularProgress size={54} sx={{ color: '#1d4ed8' }} />
              <Typography sx={{ color: '#1e40af', fontWeight: 900 }}>Kütüphanemiz hazırlanıyor...</Typography>
            </Box>
          )}
          {!loading && error && (
            <Box role="alert" sx={{ maxWidth: 620, mx: 'auto', mt: 4, p: 2, borderRadius: 3, bgcolor: '#fff7ed', color: '#9a3412', border: '2px solid #fdba74', fontWeight: 800 }}>
              {error}
            </Box>
          )}
          {!loading && !error && library && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: { xs: 2, sm: 3 }, textAlign: 'center', flexWrap: 'wrap' }}>
                <AutoStoriesRoundedIcon sx={{ color: '#1d4ed8', fontSize: { xs: 37, sm: 46 } }} />
                <Typography sx={{ color: '#1e3a8a', fontSize: { xs: 25, sm: 33 }, fontWeight: 1000 }}>
                  Birlikte {Number(library.total_books || 0)} kitap, {Number(library.total_pages || 0)} sayfa okuduk!
                </Typography>
              </Box>
              {students.length ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))', gap: { xs: 1.3, sm: 1.8 } }}>
                  {students.map((student) => <StudentLibraryCard key={student.id} student={student} />)}
                </Box>
              ) : (
                <Typography sx={{ textAlign: 'center', color: '#1e40af', fontWeight: 800, mt: 5 }}>Bu sınıfta gösterilecek öğrenci bulunmuyor.</Typography>
              )}
            </>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
}
